import React, { useState } from 'react';
import { Layout } from 'antd';
import { Routes, Route, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopNavigation from './TopNavigation';
import BusinessUnits from './BusinessUnits';
import BusinessUnitSettings from './BusinessUnitSettings';
import EditBusinessUnit from './EditBusinessUnit';
import ParticipantManagement from './ParticipantManagement';
import EditParticipant from './EditParticipant';
import MemberDetails from './MemberDetails';
import EditTeamMember from './EditTeamMember';
import Team from './Team';
import Groups from './Groups';
import AuditTrail from './AuditTrail';
import ApiActivity from './ApiActivity';
import PageHeader from './PageHeader';
import QuestionnaireList from './QuestionnaireList';
import QuestionnaireEditor from './QuestionnaireEditor';
import QuestionnaireViewer from './QuestionnaireViewer';
import QuestionnairePreviewPage from './QuestionnairePreviewPage';
import QuizPlayer from './QuizPlayer';
import CampaignList from './CampaignList';
import CampaignForm from './CampaignForm';
import CampaignReport from './CampaignReport';
import Reports from './Reports';
import ReportsResults from './ReportsResults';
import UserProfile from './UserProfile';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import authService from '../services/authService';

const { Content } = Layout;

const Dashboard = ({ user, onLogout, onLoginSuccess }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();

  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true); // Auto-collapse on mobile
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial state

    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determine selected page based on current path
  const getSelectedPage = () => {
    const path = location.pathname;
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/business-units')) return 'business-units';
    if (path.includes('/team')) return 'team';
    if (path.includes('/groups')) return 'team-groups';
    if (path.includes('/campaigns')) return 'campaigns';
    if (path.includes('/questionnaires')) return 'questionnaires';
    if (path.includes('/reports')) return 'reports';
    if (path.includes('/audit-trail')) return 'audit-trail';
    if (path.includes('/getting-started')) return 'getting-started';
    return 'business-units'; // default
  };

  const handleMenuSelect = (key) => {
    // This will be handled by React Router navigation in Sidebar
  };

  const renderContent = () => {
    return (
      <Routes>
        <Route path="/" element={<BusinessUnits />} />
        <Route path="/business-units" element={<BusinessUnits />} />
        <Route path="/business-units/new" element={<EditBusinessUnit />} />
        <Route path="/business-units/:id" element={<EditBusinessUnit />} />
        {/* Business unit related routes - more specific routes first */}
        <Route path="/businessunit/:id/members" element={<ParticipantManagement />} />
        <Route path="/businessunit/:id" element={<ParticipantManagement />} />
        {/* Settings routes */}
        <Route path="/settings/business-units" element={<BusinessUnitSettings />} />
        {/* Member related routes - more specific routes first */}
        <Route path="/member/new" element={<EditParticipant />} />
        <Route path="/member/:id/edit" element={<EditParticipant />} />
        <Route path="/member/:id" element={<MemberDetails />} />
        {/* Business unit specific member routes */}
        <Route path="/businessunit/:businessUnitId/member/new" element={<EditParticipant />} />
        <Route path="/businessunit/:businessUnitId/member/:id/edit" element={<EditParticipant />} />
        <Route path="/businessunit/:businessUnitId/member/:id" element={<MemberDetails />} />
        {/* Other routes */}
        <Route path="/team" element={<Team />} />
        <Route path="/team/edit/:id" element={<EditTeamMember />} />
        <Route path="/team-groups" element={<Groups />} />
        <Route path="/campaigns" element={<CampaignList />} />
        <Route path="/campaigns/new" element={<CampaignForm />} />
        <Route path="/campaigns/edit/:id" element={<CampaignForm />} />
        <Route path="/campaigns/report/:id" element={<CampaignReport />} />
        <Route path="/questionnaires" element={<QuestionnaireList />} />
        <Route path="/questionnaires/new" element={<QuestionnaireEditor />} />
        <Route path="/questionnaires/edit/:id" element={<QuestionnaireEditor />} />
        <Route path="/questionnaires/view/:id" element={<QuestionnaireViewer />} />
        <Route path="/questionnaires/preview/:id" element={<QuestionnairePreviewPage />} />
        <Route path="/questionnaires/take/:id" element={<QuizPlayer />} />
        <Route path="/audit-trail" element={<AuditTrail />} />
        <Route path="/api-activity" element={<ApiActivity />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/settings" element={
          <div style={{ padding: '24px' }}>
            <PageHeader 
              title="Settings" 
              subtitle="Configure your application settings"
              breadcrumbs={BREADCRUMB_PATTERNS.settings}
            />
          </div>
        } />
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/results" element={<ReportsResults />} />
        <Route path="/getting-started" element={
          <div style={{ padding: '24px' }}>
            <PageHeader 
              title="Getting Started" 
              subtitle="Learn how to use TapLearnGo"
              breadcrumbs={['Dashboard', 'Getting Started']}
            />
          </div>
        } />
      </Routes>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar
        collapsed={collapsed}
        onCollapse={setCollapsed}
        selectedKey={getSelectedPage()}
        onMenuSelect={handleMenuSelect}
      />
      
      <Layout style={{ 
        marginLeft: isMobile ? 0 : (collapsed ? 80 : 250), 
        transition: 'margin-left 0.2s' 
      }}>
        <TopNavigation 
          user={user} 
          onLogout={onLogout}
        />
        
        <Content
          style={{
            marginTop: '64px',
            background: '#F0F4F8',
            minHeight: 'calc(100vh - 64px)',
            padding: isMobile ? '8px' : '0',
          }}
        >
          <div style={{
            maxWidth: 1200,
            margin: '0 auto',
            width: '100%',
          }}>
            {renderContent()}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default Dashboard;